import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { TextLines } from "@/components/tool-kit/diagrams/svg-parts";
import { LINE_HEIGHT } from "@/lib/tool-kit/charts/layout";
import {
  DIAMOND_HALF_HEIGHT,
  FOREST_FONT,
  type ForestHeaders,
  type ForestLayout,
} from "@/lib/tool-kit/diagrams-more/forest";
import { titledFrame } from "@/lib/tool-kit/diagrams-more/frame";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A forest plot from `layoutForest`: study names at the left, then the plot — a square at
 * each estimate (sized by weight when weights were given) on a line to its limits, a
 * diamond for a summary row, the line of no effect, and an axis — then the numbers as
 * text, so every value can be read without measuring the picture.
 */

const LINE = FOREST_FONT * LINE_HEIGHT;

export function ForestDiagram({
  id,
  layout,
  headers,
  color,
  title,
  label,
  description,
}: {
  id: string;
  layout: ForestLayout;
  headers: ForestHeaders;
  color: string;
  title: string;
  label: string;
  description: string;
}) {
  const frame = titledFrame(layout.width, layout.height, title, 420);
  const { plot } = layout;
  const ruleY = plot.y - 2;
  const axisRight = plot.x + plot.width;
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitle title={frame.title} />
      <g transform={`translate(${px(frame.dx)} ${px(frame.dy)})`}>
        <g fill={CHART_INK.text} fontSize={FOREST_FONT} fontWeight="bold">
          <text
            x={px(layout.nameX)}
            y={px(layout.headerY + FOREST_FONT * 0.35)}
          >
            {headers.study}
          </text>
          <text
            x={px(layout.estimateX)}
            y={px(layout.headerY + FOREST_FONT * 0.35)}
          >
            {headers.estimate}
          </text>
          {layout.weightX === null ? null : (
            <text
              x={px(layout.weightX)}
              y={px(layout.headerY + FOREST_FONT * 0.35)}
            >
              {headers.weight}
            </text>
          )}
        </g>
        <line
          stroke={CHART_INK.text}
          strokeWidth={1}
          x1={px(layout.nameX)}
          x2={px(layout.width - layout.nameX)}
          y1={px(ruleY)}
          y2={px(ruleY)}
        />
        <line
          stroke={CHART_INK.text}
          strokeWidth={1}
          x1={px(layout.nullX)}
          x2={px(layout.nullX)}
          y1={px(plot.y)}
          y2={px(layout.axisY)}
        />
        {layout.rows.map((entry) => {
          const summary = entry.row.summary;
          const nameTop = entry.centreY - (entry.lines.length * LINE) / 2;
          return (
            <g key={`row-${entry.row.line}`}>
              <TextLines
                anchor="start"
                bold={summary}
                fill={CHART_INK.text}
                fontSize={FOREST_FONT}
                lineHeight={LINE}
                lines={entry.lines}
                top={nameTop}
                x={layout.nameX}
              />
              {summary ? (
                <polygon
                  fill={CHART_INK.text}
                  points={`${px(entry.xLower)},${px(entry.centreY)} ${px(entry.x)},${px(entry.centreY - DIAMOND_HALF_HEIGHT)} ${px(entry.xUpper)},${px(entry.centreY)} ${px(entry.x)},${px(entry.centreY + DIAMOND_HALF_HEIGHT)}`}
                />
              ) : (
                <g>
                  <line
                    stroke={CHART_INK.text}
                    strokeWidth={1.5}
                    x1={px(entry.xLower)}
                    x2={px(entry.xUpper)}
                    y1={px(entry.centreY)}
                    y2={px(entry.centreY)}
                  />
                  <rect
                    fill={color}
                    height={px(entry.half * 2)}
                    stroke={CHART_INK.background}
                    strokeWidth={0.75}
                    width={px(entry.half * 2)}
                    x={px(entry.x - entry.half)}
                    y={px(entry.centreY - entry.half)}
                  />
                </g>
              )}
              <text
                fill={CHART_INK.text}
                fontSize={FOREST_FONT}
                fontWeight={summary ? "bold" : undefined}
                x={px(layout.estimateX)}
                y={px(entry.centreY + FOREST_FONT * 0.35)}
              >
                {entry.text}
              </text>
              {layout.weightX === null ? null : (
                <text
                  fill={CHART_INK.text}
                  fontSize={FOREST_FONT}
                  x={px(layout.weightX)}
                  y={px(entry.centreY + FOREST_FONT * 0.35)}
                >
                  {entry.weightText}
                </text>
              )}
            </g>
          );
        })}
        <line
          stroke={CHART_INK.text}
          strokeWidth={1}
          x1={px(plot.x)}
          x2={px(axisRight)}
          y1={px(layout.axisY)}
          y2={px(layout.axisY)}
        />
        {layout.ticks.map((tick) => (
          <g key={`tick-${tick.label}-${px(tick.x)}`}>
            <line
              stroke={CHART_INK.text}
              strokeWidth={1}
              x1={px(tick.x)}
              x2={px(tick.x)}
              y1={px(layout.axisY)}
              y2={px(layout.axisY + 5)}
            />
            <text
              fill={CHART_INK.text}
              fontSize={FOREST_FONT}
              textAnchor="middle"
              x={px(tick.x)}
              y={px(layout.axisY + 5 + 4 + FOREST_FONT * 0.8)}
            >
              {tick.label}
            </text>
          </g>
        ))}
        {layout.leftLabel ? (
          <text
            fill={CHART_INK.text}
            fontSize={FOREST_FONT}
            textAnchor="start"
            x={px(layout.leftLabel.x)}
            y={px(layout.leftLabel.y)}
          >
            {`← ${layout.leftLabel.text}`}
          </text>
        ) : null}
        {layout.rightLabel ? (
          <text
            fill={CHART_INK.text}
            fontSize={FOREST_FONT}
            textAnchor="end"
            x={px(layout.rightLabel.x)}
            y={px(layout.rightLabel.y)}
          >
            {`${layout.rightLabel.text} →`}
          </text>
        ) : null}
        {layout.xTitle ? (
          <text
            fill={CHART_INK.text}
            fontSize={FOREST_FONT}
            textAnchor="middle"
            x={px(layout.xTitle.x)}
            y={px(layout.xTitle.y)}
          >
            {layout.xTitle.text}
          </text>
        ) : null}
      </g>
    </ChartSvg>
  );
}
